import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TrendingUp, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const BoostVideoServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    youtube_link: '',
    target_country: '',
    views_count: 1000
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Calcul automatique du prix
  const calculatePrice = () => {
    const views = parseInt(formData.views_count) || 1000;
    return Math.ceil((views / 1000) * 15);
  };
  
  const totalPrice = calculatePrice();

  const countries = [
    'RD Congo 🇨🇩', 'France 🇫🇷', 'Belgique 🇧🇪', 'Canada 🇨🇦', 
    'États-Unis 🇺🇸', 'Cameroun 🇨🇲', 'Côte d\'Ivoire 🇨🇮', 
    'Sénégal 🇸🇳', 'Gabon 🇬🇦', 'Mondial 🌍'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.youtube_link.includes('youtube.com') && !formData.youtube_link.includes('youtu.be')) {
      toast.error('Veuillez entrer un lien YouTube valide');
      return;
    }
    
    if (formData.views_count < 1000) {
      toast.error('Minimum 1000 vues requis');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/service/boost/submit', {
        ...formData,
        total_price: totalPrice
      });
      
      setSubmitted(true);
      toast.success('✅ Demande soumise ! Procédez au paiement');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Demande Validée ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Procédez au paiement pour lancer le boost de votre vidéo.
              </p>
              <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                <div className="text-sm text-zinc-400 mb-2">
                  {formData.views_count.toLocaleString()} vues demandées
                </div>
                <p className="text-4xl font-bold text-[#F97316] mb-4">{totalPrice}$</p>
                <Button className="w-full bg-gradient-to-r from-yellow-600 to-yellow-800 hover:opacity-90 py-6 text-lg">
                  PAYER {totalPrice}$ MAINTENANT
                </Button>
              </div>
              <div className="bg-green-900/20 border border-green-800 rounded-lg p-4 text-sm text-green-200 mb-6">
                <strong>✅ Garantie :</strong> Minimum 1000 vues réelles ou on relance la campagne gratuitement !
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <TrendingUp className="text-yellow-500" size={32} />
              Promouvoir Ma Vidéo - dès 15$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              À partir de 1000 vues | 15$ / 1000 vues | Publicité Google Ads
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Lien de votre Vidéo YouTube *</Label>
                <Input
                  type="url"
                  value={formData.youtube_link}
                  onChange={(e) => setFormData({...formData, youtube_link: e.target.value})}
                  required
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Copiez-collez le lien complet de votre vidéo YouTube
                </p>
              </div>

              <div>
                <Label>Combien de vues voulez-vous ? *</Label>
                <Input
                  type="number"
                  min="1000"
                  step="1000"
                  value={formData.views_count}
                  onChange={(e) => setFormData({...formData, views_count: parseInt(e.target.value) || 1000})}
                  required
                  placeholder="1000"
                  className="bg-zinc-900 border-zinc-800 text-white text-2xl font-bold"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Minimum 1000 vues | Recommandé : par tranche de 1000
                </p>
                
                {/* Affichage prix automatique */}
                <div className="mt-3 bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-400">Prix calculé automatiquement :</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {formData.views_count.toLocaleString()} vues × (15$ / 1000)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-bold text-yellow-400">{totalPrice}$</p>
                    </div>
                  </div>
                </div>
                
                {/* Exemples */}
                <div className="mt-2 text-xs text-zinc-500 space-y-1">
                  <p>💡 Exemples :</p>
                  <p>• 1,000 vues = 15$ | 10,000 vues = 150$</p>
                  <p>• 100,000 vues = 1,500$ | 1,000,000 vues = 15,000$</p>
                </div>
              </div>

              <div>
                <Label>Pays Ciblé pour la Publicité *</Label>
                <Select value={formData.target_country} onValueChange={(value) => setFormData({...formData, target_country: value})}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectValue placeholder="Choisir un pays" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {countries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">
                  Choisissez "Mondial 🌍" pour cibler tous les pays
                </p>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-6">
                <h3 className="font-bold text-white text-lg mb-3">🚀 Ce que vous obtenez :</h3>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong>{formData.views_count.toLocaleString()} vues réelles</strong> garanties</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Publicité Google Ads sur YouTube</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Ciblage géographique précis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Augmentation de l'engagement (likes, commentaires)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong className="text-yellow-300">Garantie :</strong> Nombre de vues garanties ou on relance gratuitement</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 text-sm text-blue-200">
                <strong>⏱️ Délai :</strong> Les vues commencent à arriver sous 24-48h. Campagne complète en 7-10 jours.
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-yellow-600 to-yellow-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'CONTINUER VERS LE PAIEMENT'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BoostVideoServicePage;
